import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LinkButton } from './Button';

const TableWrapper = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;

  @media (max-width: 600px) {
    padding: 0 32px;
  }
`;

const DetailsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const DetailItem = styled.li`
  color: ${p => (p.strikethrough ? '#ccc' : 'currentColor')};
  font-size: 15px;
  padding: 5px 0 5px 5px;
  text-align: left;
  text-decoration: ${p => (p.strikethrough ? 'line-through' : 'none')};
  position: relative;

  &:after {
    border: solid 2px ${p => (p.strikethrough ? 'transparent' : 'currentColor')};
    content: '';
    border-radius: 20px;
    display: inline-block;
    height: 16px;
    margin-right: 10px;
    transform: rotate(45deg);
    position: absolute;
    left: 0px;
    top: 8px;
    z-index: 0;
    width: 16px;
  }
  &:before {
    border-bottom: solid 2px
      ${p => (p.strikethrough ? 'transparent' : 'currentColor')};
    border-right: solid 2px
      ${p => (p.strikethrough ? 'transparent' : 'currentColor')};
    content: '';
    display: inline-block;
    height: 9px;
    margin-left: 2px;
    margin-right: 15px;
    position: relative;
    transform: rotate(45deg);
    width: 4px;
  }
`;

const Header = styled.div`
  border-bottom: solid 1px #f2f2f2;
  font-family: secondarytypeface;
  font-size: 24px;
  margin-bottom: 20px;
  margin-top: 0;
  padding-bottom: 20px;
  text-align: left;
`;

const Title = styled.h1`
  font-size: 32px;
  margin: 0;
`;

const PriceWrapper = styled.div`
  align-items: center;
  display: flex;
  font-family: secondarytypeface;
  font-size: 34px;
`;

const Price = styled.div`
  color: currentColor;
  font-size: 22px;
  margin: 0;
  padding-right: 6px;
`;

const ComparePrice = styled(Price)`
  color: currentColor;
  display: flex;
  font-size: 16px;
  margin-left: 22px;
  opacity: 0.75;
  padding-right: 0;
  text-decoration-color: #444;
  text-decoration: line-through;
`;

const BillingPeriod = styled.div`
  color: #aaaaaa;
  font-size: 16px;
`;

const PackageCard = styled.div`
  --background: #fff;
  --border: #f2f2f2;
  --foreground: #333;
  ${p =>
    p.contrast &&
    `
    --background: var(--primary-500);
    --border: transparent;
    --foreground: #fff;
  `}
  background-color: var(--background);
  border: solid 1px var(--border);
  border-radius: 14px;
  color: var(--foreground);
  flex: 1 0;
  min-width: 280px;
  padding: 22px 32px;
  scroll-snap-align: center;
  opacity: ${p => (p.isDisabled ? 0.5 : 1)};
`;

const Actions = styled.div`
  padding: 40px 40px 20px;
  text-align: center;
`;

const Description = styled.div`
  color: currentColor;
  font-size: 14px;
  max-width: 80%;
  padding: 12px 0;
  font-weight: bold;
`;

/*
 * Pricing Table Components
 */
const PricingTable = ({ packages = [] }) => {
  return (
    <TableWrapper>
      {packages.map((props, index) => (
        <PricingTable.Package key={index} {...props} />
      ))}
    </TableWrapper>
  );
};
PricingTable.propTypes = {
  packages: PropTypes.array,
};

PricingTable.Package = ({
  buttonLabel = 'Sign Up',
  comparePrice,
  contrast = false,
  description,
  href,
  includedFeatures = [],
  isDisabled = false,
  notIncludedFeatures = [],
  period,
  price,
  showButton = false,
  title,
}) => {
  return (
    <PackageCard isDisabled={isDisabled} contrast={contrast}>
      <Header>
        <Title>{title}</Title>
        <PriceWrapper>
          <Price>{price}</Price>
          <BillingPeriod>{period}</BillingPeriod>
          {!!comparePrice && (
            <>
              <ComparePrice>
                {comparePrice}
                <BillingPeriod>{period}</BillingPeriod>
              </ComparePrice>
            </>
          )}
        </PriceWrapper>
      </Header>
      <Description>{description}</Description>
      <DetailsList>
        {includedFeatures.map((feature, index) => {
          return <DetailItem key={index}>{feature}</DetailItem>;
        })}
        {notIncludedFeatures.map((feature, index) => {
          return (
            <DetailItem strikethrough key={index}>
              {feature}
            </DetailItem>
          );
        })}
      </DetailsList>
      <Actions>
        {showButton && !isDisabled && (
          <LinkButton href={href} color="primary" block={false} size="large">
            {buttonLabel}
          </LinkButton>
        )}
      </Actions>
    </PackageCard>
  );
};
PricingTable.Package.propTypes = {
  buttonLabel: PropTypes.string,
  comparePrice: PropTypes.string,
  description: PropTypes.string,
  href: PropTypes.string,
  includedFeatures: PropTypes.array,
  isDisabled: PropTypes.bool,
  notIncludedFeatures: PropTypes.array,
  period: PropTypes.string,
  price: PropTypes.string,
  showButton: PropTypes.bool,
  title: PropTypes.string,
};

export default PricingTable;
